// Copyright © 2021 React Training LLC. All rights reserved.
import React from 'react';

class RemixErrorBoundary extends React.Component {
  state = {
    error: this.props.error || null,
    location: this.props.location
  };

  static getDerivedStateFromError(error) {
    return {
      error
    };
  }

  static getDerivedStateFromProps(props, state) {
    // When we get into an error state, the user will likely click "back" to the
    // previous page that didn't have an error. Because this wraps the entire
    // application (even the HTML!) that will have no effect--the error page
    // continues to display. This gives us a mechanism to recover from the error
    // when the location changes.
    //
    // Whether we're in an error state or not, we update the location in state
    // so that when we are in an error state, it gets reset when a new location
    // comes in and the user recovers from the error.
    if (state.location !== props.location) {
      return {
        error: props.error || null,
        location: props.location
      };
    }

    return state;
  }

  render() {
    if (this.state.error) {
      return /*#__PURE__*/React.createElement(this.props.component, {
        error: this.state.error
      });
    } else {
      return this.props.children;
    }
  }

}
/**
 * When app's don't provide a root level ErrorBoundary, we default to this.
 */

function RemixRootDefaultErrorBoundary({
  error
}) {
  console.error(error);
  return /*#__PURE__*/React.createElement("html", {
    lang: "en"
  }, /*#__PURE__*/React.createElement("head", null, /*#__PURE__*/React.createElement("meta", {
    charSet: "utf-8"
  }), /*#__PURE__*/React.createElement("title", null, "Uncaught Exception!")), /*#__PURE__*/React.createElement("body", null, /*#__PURE__*/React.createElement("main", {
    style: {
      border: "solid 2px hsl(10, 50%, 50%)",
      padding: "2rem"
    }
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h1", null, "Uncaught Exception!"), /*#__PURE__*/React.createElement("p", null, "If you are not the developer, please click back in your browser and try again."), /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: `"SFMono-Regular",Consolas,"Liberation Mono",Menlo,Courier,monospace`,
      padding: "1rem",
      margin: "1rem 0",
      border: "solid 4px"
    }
  }, error.message), /*#__PURE__*/React.createElement("p", null, "There was an uncaught exception in your application. Check the browser console and/or server console to inspect the error."), /*#__PURE__*/React.createElement("p", null, "If you are the developer, consider adding your own error boundary so users don't see this page when unexpected errors happen in production!"), /*#__PURE__*/React.createElement("p", null, "Read more about", " ", /*#__PURE__*/React.createElement("a", {
    target: "_blank",
    rel: "noreferrer",
    href: "https://remix.run/dashboard/docs/errors"
  }, "Error Handling in Remix"), ".")))));
}

export { RemixErrorBoundary, RemixRootDefaultErrorBoundary };
